import React from 'react';
import { NodeInfo } from '../Interfaces/Assets/AssetInfo';
import { Button } from '@progress/kendo-react-buttons';
import { GetString } from './StringResourceManager';
import { ApiEnums } from '../Interfaces/ApiEnums';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { GridLayout, GridLayoutItem } from '@progress/kendo-react-layout';
import { getLocalDateTime } from '../utils/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    confirmationDialog: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      flexWrap: 'wrap',
    },
    confirmationDialogTitle: {
      minWidth: '650px',
      fontSize: '24px',
      textAlign: 'center',
    },
    confirmationDialogButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 10,
    },
    confirmationClosePromptButtonLeft: {
      height: '40px',
      fontSize: '20px',
      width: '150px',
      marginRight: 10,
    },
    confirmationClosePromptButtonRight: {
      height: '40px',
      fontSize: '20px',
      width: '150px',
      marginLeft: 10,
    },
    confirmationClosePromptGridLayout: {
      fontSize: '20px',
      padding: '50px',
      fontWeight: 'bold',
    },
    confirmationClosePromptLabel: {
      border: '1px solid #ccc',
      textAlign: 'center',
    },
    confirmationClosePromptValue: {
      border: '1px solid #ccc',
      textAlign: 'center',
      color: 'darkblue',
      fontSize: '20px',
    },
  })
);

interface Props {
  node: NodeInfo;
  handleActionButtonClick: (
    actionType: ApiEnums.ManagementActions,
    uuid: string
  ) => void;
}

const NodeActionButton: React.FC<Props> = ({
  node,
  handleActionButtonClick,
}): React.ReactElement => {
  const classes = useStyles();
  const [showConfirmationDialog, setShowConfirmationDialog] =
    React.useState(false);
  const DIVISION = GetString('COLUMN_TITLE_DIVISION');
  const STORE = GetString('STORE_TITLE');
  const LANE = GetString('LANE_TITLE');
  const NODE_TYPE = GetString('NODE_TYPE_TITLE');
  const CLUSTER_TITLE = GetString('CLUSTER_TITLE');
  const DECOMMISSION_NODE = GetString('DECOMMISSION_NODE');
  const REACTIVATE_NODE = GetString('REACTIVATE_NODE');
  const NODE_CONFIRM_TITLE = GetString('NODE_UPDATE_TITLE');
  const COLUMN_TITLE_LANE_LAST_REFRESHED = GetString(
    'COLUMN_TITLE_LANE_LAST_REFRESHED'
  );
  const COLUMN_TITLE_MACHINE_NAME = GetString('COLUMN_TITLE_MACHINE_NAME');
  const DECOMMISSION_CONFIRMATION_MESSAGE = GetString(
    'DECOMMISSION_CONFIRMATION_MESSAGE'
  );
  const REACTIVATE_CONFIRMATION_MESSAGE = GetString(
    'REACTIVATE_CONFIRMATION_MESSAGE'
  );

  const YES = GetString('YES');
  const NO = GetString('NO');

  const handleButtonClick = () => {
    setShowConfirmationDialog(true);
  };

  const handleConfirmationDialogYes = (
    actionType: ApiEnums.ManagementActions,
    uuid: string
  ) => {
    handleActionButtonClick(actionType, uuid);
    setShowConfirmationDialog(false);
  };

  const handleClose = () => {
    setShowConfirmationDialog(false);
  };

  const getGridLayout = () => {
    return (
      <GridLayout
        cols={[{ width: 200 }, { width: 350 }]}
        className={classes.confirmationClosePromptGridLayout}
        align={{ horizontal: 'stretch' }}
      >
        <GridLayoutItem
          col={1}
          colSpan={1}
          className={classes.confirmationClosePromptLabel}
        >
          {DIVISION}
        </GridLayoutItem>
        <GridLayoutItem
          col={2}
          colSpan={1}
          className={classes.confirmationClosePromptValue}
        >
          {node?.Division}
        </GridLayoutItem>

        <GridLayoutItem
          col={1}
          colSpan={1}
          className={classes.confirmationClosePromptLabel}
        >
          {STORE}
        </GridLayoutItem>
        <GridLayoutItem
          col={2}
          colSpan={1}
          className={classes.confirmationClosePromptValue}
        >
          {node?.SiteNumber}
        </GridLayoutItem>

        <GridLayoutItem
          col={1}
          colSpan={1}
          className={classes.confirmationClosePromptLabel}
        >
          {CLUSTER_TITLE}
        </GridLayoutItem>
        <GridLayoutItem
          col={2}
          colSpan={1}
          className={classes.confirmationClosePromptValue}
        >
          {node?.NodeGroup}
        </GridLayoutItem>

        <GridLayoutItem
          col={1}
          colSpan={1}
          className={classes.confirmationClosePromptLabel}
        >
          {LANE}
        </GridLayoutItem>
        <GridLayoutItem
          col={2}
          colSpan={1}
          className={classes.confirmationClosePromptValue}
        >
          {node?.LaneNumber}
        </GridLayoutItem>

        <GridLayoutItem
          col={1}
          colSpan={1}
          className={classes.confirmationClosePromptLabel}
        >
          {COLUMN_TITLE_MACHINE_NAME}
        </GridLayoutItem>
        <GridLayoutItem
          col={2}
          colSpan={1}
          className={classes.confirmationClosePromptValue}
        >
          {node?.MachineName}
        </GridLayoutItem>

        <GridLayoutItem
          col={1}
          colSpan={1}
          className={classes.confirmationClosePromptLabel}
        >
          {NODE_TYPE}
        </GridLayoutItem>
        <GridLayoutItem
          col={2}
          colSpan={1}
          className={classes.confirmationClosePromptValue}
        >
          {node?.NodeType}
        </GridLayoutItem>

        <GridLayoutItem
          col={1}
          colSpan={1}
          className={classes.confirmationClosePromptLabel}
        >
          {COLUMN_TITLE_LANE_LAST_REFRESHED}
        </GridLayoutItem>
        <GridLayoutItem
          col={2}
          colSpan={1}
          className={classes.confirmationClosePromptValue}
        >
          {getLocalDateTime(node?.LastHeartbeat)}
        </GridLayoutItem>
      </GridLayout>
    );
  };

  return (
    <>
      <Button
        themeColor={'primary'}
        type="button"
        style={{
          fontSize: '0.9rem',
          width: '150px',
        }}
        onClick={handleButtonClick}
      >
        {node.ManagementAction === ApiEnums.ManagementActions.Active
          ? DECOMMISSION_NODE
          : REACTIVATE_NODE}
      </Button>

      {showConfirmationDialog && (
        <Dialog title={NODE_CONFIRM_TITLE} onClose={handleClose}>
          <div className={classes.confirmationDialog}>
            <div className={classes.confirmationDialogTitle}>
              {node.ManagementAction === ApiEnums.ManagementActions.Active
                ? DECOMMISSION_CONFIRMATION_MESSAGE
                : REACTIVATE_CONFIRMATION_MESSAGE}
            </div>
            {getGridLayout()}
            <DialogActionsBar>
              <div className={classes.confirmationDialogButtonContainer}>
                <Button
                  themeColor={'primary'}
                  type="button"
                  onClick={() =>
                    handleConfirmationDialogYes(
                      node.ManagementAction,
                      node.Uuid
                    )
                  }
                  className={classes.confirmationClosePromptButtonLeft}
                >
                  {YES}
                </Button>
                <Button
                  themeColor={'primary'}
                  type="button"
                  onClick={handleClose}
                  className={classes.confirmationClosePromptButtonRight}
                >
                  {NO}
                </Button>
              </div>
            </DialogActionsBar>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default NodeActionButton;
